// Registering (and signing back in) with a BIP-322 wallet signature.
//
// The wallet is the ROOT identity — no seed is created, shown, or asked for.
// The wallet signs a documented challenge string; the server verifies it in
// BIP-322 (simple, mainnet bc1q/bc1p), creates or resolves the account, and
// approves this browser's fresh DEVICE key for transport auth. One device
// per account: a login from a new browser rotates the key.
//
// Challenge strings are part of the server contract — change nothing:
//   register: `twetch-register:{handle}:{nonce}:{ts}`
//   login:    `twetch-login:{nonce}:{ts}`
// `nonce` is 32+ hex chars of client entropy, `ts` unix ms within ±5 min.

import { twetchBridgeJson, twetchJson, TwetchError } from './twetch-api'
import { newDeviceKey, randomHexNonce, storeDeviceKey, type DeviceKey } from './twetch-crypto'
import { walletSignatureString } from './wallet-signature'

export const TWETCH_ALGORITHM = 'BTC_BIP322'

/// BIP-322 simple needs a mainnet P2WPKH (bc1q…) or key-path P2TR (bc1p…)
/// address — other wallet types can't produce a signature the server accepts.
export function bip322Capable(address: string | null | undefined): boolean {
	return !!address && /^bc1[qp][a-z0-9]{6,}$/.test(address.toLowerCase())
}

export function registerChallenge(handle: string, nonce: string, ts: number): string {
	return `twetch-register:${handle}:${nonce}:${ts}`
}

export interface ProfileEdit {
	name?: string
	description?: string
	icon?: string
}

/// What the wallet signs to edit its account's profile — the values
/// themselves, byte-length framed (Twetch rebuilds this exact text).
export function profileChallenge(edit: ProfileEdit, nonce: string, ts: number): string {
	const field = (value: string | undefined) =>
		value === undefined ? '-' : `${new TextEncoder().encode(value).length}:${value}`
	return [
		`twetch-profile:${nonce}:${ts}`,
		`name:${field(edit.name)}`,
		`bio:${field(edit.description)}`,
		`picture:${field(edit.icon)}`
	].join('\n')
}

export function loginChallenge(nonce: string, ts: number): string {
	return `twetch-login:${nonce}:${ts}`
}

export type RegisterFailure =
	| 'address-registered'
	| 'handle-taken'
	| 'handle-invalid'
	| 'expired'
	| 'turnstile'
	| 'unavailable'
	| 'other'

/// Map a register refusal onto the UX branch it deserves. Status is the
/// primary signal; the server's message text disambiguates the 400/409
/// families (handle vs address vs challenge).
export function classifyRegisterError(e: unknown): RegisterFailure {
	const status = e instanceof TwetchError ? e.status : undefined
	const msg = String((e as { message?: string })?.message ?? '')
	if (/already (registered|has an account)|address .*registered/i.test(msg)) {
		return 'address-registered'
	}
	if (/handle .*(taken|in use|exists)|taken/i.test(msg)) {
		return 'handle-taken'
	}
	if (/handle|username/i.test(msg)) {
		return 'handle-invalid'
	}
	if (/expired|too old|stale|window|timestamp/i.test(msg)) {
		return 'expired'
	}
	if (/turnstile|captcha|verification|human/i.test(msg)) {
		return 'turnstile'
	}
	if (status === 409) {
		return 'address-registered'
	}
	if (status === 503) {
		return 'unavailable'
	}
	return 'other'
}

export type LoginFailure = 'no-account' | 'expired' | 'other'

export function classifyLoginError(e: unknown): LoginFailure {
	const status = e instanceof TwetchError ? e.status : undefined
	const msg = String((e as { message?: string })?.message ?? '')
	if (status === 404 || /no account/i.test(msg)) {
		return 'no-account'
	}
	if (/expired|too old|stale|window|timestamp/i.test(msg)) {
		return 'expired'
	}
	return 'other'
}

export interface TwetchIdentity {
	uid: number
	handle: string
	/// The external wallet address, AS THE WALLET RETURNED IT.
	address: string
	algorithm: typeof TWETCH_ALGORITHM
	devicePubkey: string
}

interface WalletSigner {
	/// The wallet's message-signing door (one popup). Must return the base64
	/// BIP-322 witness verbatim.
	signMessage: (text: string) => Promise<string>
}

/// Register a brand-new Twetch account rooted in the connected wallet.
/// Exactly one wallet popup (the register challenge). On success the fresh
/// device key is persisted and the identity returned.
export async function registerWithWallet(
	opts: {
		handle: string
		address: string
		turnstileToken: string
	} & WalletSigner,
	deps?: { now?: () => number; nonce?: () => string; deviceKey?: () => DeviceKey }
): Promise<TwetchIdentity> {
	const deviceKey = (deps?.deviceKey ?? newDeviceKey)()
	const nonce = (deps?.nonce ?? randomHexNonce)()
	const ts = (deps?.now ?? Date.now)()
	const signature = walletSignatureString(
		await opts.signMessage(registerChallenge(opts.handle, nonce, ts))
	)
	const { userId } = await twetchJson<{ userId: number }>('/v1/auth/register', {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({
			handle: opts.handle,
			devicePubkey: deviceKey.pub,
			// This browser key has no wallet behind it — Twetch must not route
			// like/follow payouts to it (they'd be lost with localStorage).
			sessionDeviceKey: true,
			turnstileToken: opts.turnstileToken,
			externalIdentity: {
				algorithm: TWETCH_ALGORITHM,
				address: opts.address,
				signature,
				nonce,
				ts
			}
		})
	})
	storeDeviceKey(deviceKey)
	return {
		uid: userId,
		handle: opts.handle,
		address: opts.address,
		algorithm: TWETCH_ALGORITHM,
		devicePubkey: deviceKey.pub
	}
}

/// Sign back in with an already-registered wallet. Rotates the account's
/// device key to this browser's fresh one (one device per account). The
/// server answers with the account's handle so the UI can greet properly.
export async function loginWithWallet(
	opts: { address: string } & WalletSigner,
	deps?: { now?: () => number; nonce?: () => string; deviceKey?: () => DeviceKey }
): Promise<TwetchIdentity> {
	const deviceKey = (deps?.deviceKey ?? newDeviceKey)()
	const nonce = (deps?.nonce ?? randomHexNonce)()
	const ts = (deps?.now ?? Date.now)()
	const signature = walletSignatureString(await opts.signMessage(loginChallenge(nonce, ts)))
	const res = await twetchJson<{ userId: number; handle?: string; name?: string }>(
		'/v1/auth/login-external',
		{
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify({
				algorithm: TWETCH_ALGORITHM,
				address: opts.address,
				signature,
				nonce,
				ts,
				devicePubkey: deviceKey.pub,
				sessionDeviceKey: true
			})
		}
	)
	storeDeviceKey(deviceKey)
	return {
		uid: res.userId,
		handle: res.handle ?? res.name ?? `user ${res.userId}`,
		address: opts.address,
		algorithm: TWETCH_ALGORITHM,
		devicePubkey: deviceKey.pub
	}
}

/// Save a profile edit under ONE wallet signature — no Twetch session.
///
/// Works for any wallet tied to an account, including a wallet LINKED to a
/// seed-rooted Twetch account: Twetch (rightly) refuses those a full session,
/// since opening one rotates the account's key, but lets them sign an edit of
/// exactly these values. Goes through the OW bridge, the only door Twetch
/// accepts BTC-wallet writes from.
export async function saveProfileWithWallet(
	opts: { address: string; edit: ProfileEdit } & WalletSigner,
	deps?: { now?: () => number; nonce?: () => string }
): Promise<{ id: number; name?: string | null; description?: string | null; icon?: string | null }> {
	const nonce = (deps?.nonce ?? randomHexNonce)()
	const ts = (deps?.now ?? Date.now)()
	const signature = walletSignatureString(await opts.signMessage(profileChallenge(opts.edit, nonce, ts)))
	return twetchBridgeJson('/v1/users/profile-external', {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({
			algorithm: TWETCH_ALGORITHM,
			address: opts.address,
			signature,
			nonce,
			ts,
			...opts.edit
		})
	})
}
